import { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { TIER_INFO, TIER_CREDITS, POLAR_PRODUCT_IDS } from '@n8n-builder/shared';
import { API_BASE_URL } from '../lib/api-config';
import { useCreditsStore } from '../store/credits';
import { useCredits } from '../hooks/useCredits';
import { signOut } from '../lib/auth-client';

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

function LogoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 28 28" fill="none">
      <rect width="28" height="28" rx="7" fill="#0072FF" />
      <path d="M7.5 14h6m0 0l5.5-5m-5.5 5l5.5 5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const TIER_FEATURES: Record<'pro' | 'business', string[]> = {
  pro: [
    'Unlimited workflows',
    '811+ n8n node support',
    'Plan, Build, and Edit modes',
    'Direct deployment to your n8n instance',
    'Import your existing workflows',
    'MCP access',
    'Workflow edit mode',
    'Email support',
  ],
  business: [
    'Everything in Pro, plus:',
    'Unlimited conversation history',
    'Priority processing',
    'Use the latest Opus model',
    'Connect multiple N8N instances',
    'Priority support',
  ],
};

function hasPreselectedPlan(): boolean {
  const params = new URLSearchParams(window.location.search);
  const urlPlan = params.get('plan');
  const storedPlan = localStorage.getItem('pendingPlan');
  const plan = urlPlan || storedPlan;
  return plan === 'pro' || plan === 'business' || params.get('checkout') === 'success';
}

export function PlanGate() {
  const { refetch } = useCredits();
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Start hidden if a plan is pre-selected to avoid UI flash
  const [checkoutOpen, setCheckoutOpen] = useState(hasPreselectedPlan);
  const [checkoutComplete, setCheckoutComplete] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoTriggeredRef = useRef(false);

  // Auto-trigger checkout if plan is pre-selected, or detect checkout success redirect
  useEffect(() => {
    if (autoTriggeredRef.current) return;

    const params = new URLSearchParams(window.location.search);

    // Check if returning from a successful checkout redirect
    if (params.get('checkout') === 'success') {
      autoTriggeredRef.current = true;
      window.history.replaceState({}, '', '/');
      setCheckoutComplete(true);
      return;
    }

    const urlPlan = params.get('plan') as 'pro' | 'business' | null;
    const storedPlan = localStorage.getItem('pendingPlan') as 'pro' | 'business' | null;
    const plan = urlPlan || storedPlan;

    if (plan === 'pro' || plan === 'business') {
      autoTriggeredRef.current = true;
      // Clean up
      localStorage.removeItem('pendingPlan');
      if (urlPlan) {
        const url = new URL(window.location.href);
        url.searchParams.delete('plan');
        window.history.replaceState({}, '', url.toString());
      }
      handleUpgrade(plan);
    }
  }, []);

  // Poll for subscription update after checkout
  useEffect(() => {
    if (checkoutComplete) {
      pollRef.current = setInterval(async () => {
        await refetch();
        const currentSub = useCreditsStore.getState().subscription;
        if (currentSub !== 'free') {
          if (pollRef.current) clearInterval(pollRef.current);
          // No reload needed — ProtectedRoute will stop rendering PlanGate
        }
      }, 2000);
    }

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [checkoutComplete, refetch]);

  const handleUpgrade = async (tier: 'pro' | 'business') => {
    setCheckoutLoading(tier);
    setError(null);

    try {
      const res = await fetch(`${API_BASE_URL}/api/billing/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ productId: POLAR_PRODUCT_IDS[tier] }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      const { url } = await res.json();

      // Hide PlanGate UI synchronously so the dashboard shows behind the Polar checkout overlay
      flushSync(() => {
        setCheckoutOpen(true);
      });

      const { PolarEmbedCheckout } = await import('@polar-sh/checkout/embed');
      PolarEmbedCheckout.create(url);

      // Watch for checkout dismiss (iframe removed) or success
      const handleMessage = (event: MessageEvent) => {
        if (event.data?.type === 'polar:checkout:success' || event.data === 'polar:checkout:success') {
          cleanup();
          setCheckoutComplete(true);
        }
      };
      window.addEventListener('message', handleMessage);

      // Use MutationObserver to detect when Polar removes its overlay
      const observer = new MutationObserver(() => {
        const polarOverlay = document.querySelector('polar-checkout-overlay');
        if (!polarOverlay) {
          cleanup();
          setCheckoutOpen(false);
        }
      });
      // Delay observer start slightly to let Polar inject the overlay first
      const observerTimer = setTimeout(() => {
        observer.observe(document.body, { childList: true, subtree: true });
      }, 1000);

      const cleanup = () => {
        window.removeEventListener('message', handleMessage);
        observer.disconnect();
        clearTimeout(observerTimer);
      };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setCheckoutOpen(false);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/sign-in';
  };

  // Once checkout is open or complete, hide the overlay so dashboard shows behind Polar's modal
  if (checkoutOpen || checkoutComplete) {
    return null;
  }

  const tiers = ['pro', 'business'] as const;

  return (
    <div className="fixed inset-0 z-40 flex flex-col items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-3xl">
        {/* Header */}
        <div className="text-center mb-10">
          <LogoIcon className="h-10 w-10 mx-auto mb-4" />
          <h1 className="text-3xl font-semibold text-gray-900">Choose your plan</h1>
          <p className="mt-2 text-gray-500">Select a plan to start building n8n workflows with AI.</p>
        </div>

        {error && (
          <div className="border border-red-200 rounded-xl p-3 bg-red-50 mb-6 max-w-lg mx-auto">
            <p className="text-sm text-red-700 text-center">{error}</p>
          </div>
        )}

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tiers.map((tier) => {
            const info = TIER_INFO[tier];
            const features = TIER_FEATURES[tier];
            const isPopular = tier === 'business';

            return (
              <div
                key={tier}
                className={`relative rounded-2xl p-6 flex flex-col bg-white ${
                  isPopular
                    ? 'border-2 border-primary-500 ring-1 ring-primary-500/20 shadow-md'
                    : 'border border-gray-200 shadow-sm'
                }`}
              >
                {isPopular && (
                  <span className="absolute -top-3 left-6 inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-primary-600 text-white shadow-sm">
                    Most Popular
                  </span>
                )}

                <div className="mb-5">
                  <h3 className="text-lg font-semibold text-gray-900">{info.name}</h3>
                  <div className="mt-1">
                    <span className="text-4xl font-bold text-gray-900">${info.price}</span>
                    <span className="text-sm text-gray-500">/mo</span>
                  </div>
                </div>

                <div className="mb-4 pb-3 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{TIER_CREDITS[tier]} credits/month</p>
                </div>

                <ul className="space-y-2.5 mb-7 flex-1">
                  {features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckIcon className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleUpgrade(tier)}
                  disabled={checkoutLoading !== null}
                  className="w-full rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {checkoutLoading === tier ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      Loading...
                    </span>
                  ) : (
                    `Get started with ${info.name}`
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Sign out link */}
        <div className="mt-8 text-center">
          <button
            onClick={handleSignOut}
            className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            Log out
          </button>
        </div>
      </div>
    </div>
  );
}
